import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Checkbox,
  TablePagination,
  Grid,
  Typography,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Autocomplete,
} from '@mui/material';
import {
  Search,
  FilterList,
  FileDownload,
  Group,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import { apiService, Team } from '../../services/apiService';
import { useNavigate } from 'react-router-dom';
import SettingsSidebar from '../../components/Common/SettingsSidebar';

interface MemberField {
  location_id: string;
  user_id: string;
}

const Teams: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  // Drawer states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [supervisors, setSupervisors] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  
  const [formData, setFormData] = useState({
    name: '',
    supervisor_id: '',
    status: 'active',
    notes: '',
  });
  
  const [members, setMembers] = useState<MemberField[]>([
    { location_id: '', user_id: '' }
  ]);
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch data from API
  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    fetchTeams();
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      const response = await apiService.getTeamsDropdownData();
      const data = response.data;
      setSupervisors(data.supervisors || []);
      setEmployees(data.employees || []);
      setLocations(data.locations || []);
    } catch (error: any) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTeams();
      const teamsData = Array.isArray(response.data) ? response.data : [];
      setTeams(teamsData);
    } catch (error: any) {
      console.error('Error fetching teams:', error);
      if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      } else {
        toast.error(error.message || 'Failed to fetch teams');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDrawer = (team?: Team) => {
    if (team) {
      setIsEditing(true);
      setEditingTeam(team);
      setFormData({
        name: team.name,
        supervisor_id: team.supervisor_id.toString(),
        status: team.status,
        notes: team.notes || '',
      });
      // Set members for editing
      if (team.members && team.members.length > 0) {
        setMembers(team.members.map(m => ({
          location_id: m.location_id.toString(),
          user_id: m.user_id.toString(),
        })));
      } else {
        setMembers([{ location_id: '', user_id: '' }]);
      }
    } else {
      setIsEditing(false);
      setEditingTeam(null);
      setFormData({
        name: '',
        supervisor_id: '',
        status: 'active',
        notes: '',
      });
      setMembers([{ location_id: '', user_id: '' }]);
    }
    setErrors({});
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setIsEditing(false);
    setEditingTeam(null);
    setFormData({
      name: '',
      supervisor_id: '',
      status: 'active',
      notes: '',
    });
    setMembers([{ location_id: '', user_id: '' }]);
    setErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleMemberChange = (index: number, field: keyof MemberField, value: string) => {
    const updatedMembers = [...members];
    updatedMembers[index] = { ...updatedMembers[index], [field]: value };
    setMembers(updatedMembers);
    if (errors[`members.${index}.${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`members.${index}.${field}`];
        return newErrors;
      });
    }
  };

  const handleAddMember = () => {
    setMembers([...members, { location_id: '', user_id: '' }]);
  };

  const handleRemoveMember = (index: number) => {
    if (members.length > 1) {
      setMembers(members.filter((_, i) => i !== index));
    } else {
      toast.error('At least one team member is required');
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Team Name is required';
    }
    if (!formData.supervisor_id) {
      newErrors.supervisor_id = 'Supervisor is required';
    }
    members.forEach((member, index) => {
      if (!member.location_id) {
        newErrors[`members.${index}.location_id`] = 'Location is required';
      }
      if (!member.user_id) {
        newErrors[`members.${index}.user_id`] = 'Employee is required';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setSubmitting(true);
    try {
      const teamData = {
        name: formData.name,
        supervisor_id: parseInt(formData.supervisor_id),
        status: formData.status,
        notes: formData.notes,
        members: members.map(m => ({
          location_id: parseInt(m.location_id),
          user_id: parseInt(m.user_id),
        })),
      };

      if (isEditing && editingTeam) {
        await apiService.updateTeam(editingTeam.id, teamData);
        toast.success('Team updated successfully');
      } else {
        await apiService.createTeam(teamData);
        toast.success('Team created successfully');
      }
      
      handleCloseDrawer();
      fetchTeams();
    } catch (error: any) {
      console.error('Error saving team:', error);
      if (error.data && error.data.errors) {
        const apiErrors = error.data.errors;
        const newErrors: Record<string, string> = {};
        Object.keys(apiErrors).forEach(key => {
          newErrors[key] = Array.isArray(apiErrors[key]) ? apiErrors[key][0] : apiErrors[key];
        });
        setErrors(newErrors);
      } else {
        toast.error(error.message || 'Failed to save team');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTeam = async (teamId: number) => {
    const team = teams.find(t => t.id === teamId);
    const teamName = team ? team.name : 'this team';
    
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${teamName}. This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await apiService.deleteTeam(teamId);
        
        Swal.fire({
          title: 'Deleted!',
          text: 'Team has been deleted successfully.',
          icon: 'success',
          confirmButtonColor: '#3085d6',
        });
        
        fetchTeams();
      } catch (error: any) {
        console.error('Error deleting team:', error);
        
        Swal.fire({
          title: 'Error!',
          text: error.message || 'Failed to delete team',
          icon: 'error',
          confirmButtonColor: '#3085d6',
        });
      }
    }
  };

  const filteredTeams = teams.filter(team =>
    team.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.supervisor?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (team.members && team.members.length > 0 && team.members.length.toString().includes(searchTerm))
  );

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1f2937', fontSize: '2rem' }}>
          Settings
        </Typography>
        <Button
          variant="contained"
          startIcon={<Group />}
          onClick={() => handleOpenDrawer()}
          sx={{ 
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            backgroundColor: '#4A90E2',
            color: 'white',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#3b82f6',
            },
          }}
        >
          New Teams
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Sidebar - Settings Menu */}
        <Grid size={{ xs: 12, md: 3 }}>
          <SettingsSidebar />
        </Grid>

        {/* Right Content Area */}
        <Grid size={{ xs: 12, md: 9 }}>
          {/* Overview Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white',
            border: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ 
                color: '#64748b', 
                fontSize: '1rem',
                fontWeight: 500,
                letterSpacing: '0.02em'
              }}>
                Total Teams
              </Typography>
              <Typography variant="h2" sx={{ 
                color: '#1e40af', 
                fontWeight: 'bold',
                fontSize: '2rem',
                lineHeight: 1.2,
                letterSpacing: '-0.02em'
              }}>
                {teams.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white',
            border: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ 
                color: '#64748b', 
                fontSize: '1rem',
                fontWeight: 500,
                letterSpacing: '0.02em'
              }}>
                Active Teams
              </Typography>
              <Typography variant="h2" sx={{ 
                color: '#1e40af', 
                fontWeight: 'bold',
                fontSize: '2rem',
                lineHeight: 1.2,
                letterSpacing: '-0.02em'
              }}>
                {teams.filter(team => team.status === 'active').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white',
            border: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ 
                color: '#64748b', 
                fontSize: '1rem',
                fontWeight: 500,
                letterSpacing: '0.02em'
              }}>
                Total Members
              </Typography>
              <Typography variant="h2" sx={{ 
                color: '#1e40af', 
                fontWeight: 'bold',
                fontSize: '2rem',
                lineHeight: 1.2,
                letterSpacing: '-0.02em'
              }}>
                {teams.reduce((total, team) => total + (team.members?.length || 0), 0)}
              </Typography>
            </CardContent>
          </Card>
          </Grid>
          </Grid>

          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 3 }}>
          {/* Search and Action Bar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                placeholder="Search here"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#4A90E2' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  width: 400,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: '#e0f2fe',
                    borderColor: '#e0f2fe',
                    '& fieldset': {
                      borderColor: '#e0f2fe',
                    },
                    '&:hover fieldset': {
                      borderColor: '#bae6fd',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4A90E2',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#374151',
                    '&::placeholder': {
                      color: '#9ca3af',
                      opacity: 1,
                    },
                  },
                }}
              />
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                sx={{ 
                  px: 3,
                  py: 1.5,
                  borderRadius: '12px',
                  borderColor: '#e0f2fe',
                  backgroundColor: 'white',
                  color: '#4A90E2',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#bae6fd',
                    backgroundColor: '#f0f9ff',
                  },
                }}
              >
                Filter
              </Button>
            </Box>
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              sx={{ 
                px: 3,
                py: 1.5,
                borderRadius: '12px',
                backgroundColor: '#4A90E2',
                color: 'white',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#3b82f6',
                },
              }}
            >
              Export
            </Button>
          </Box>

          {/* Data Table */}
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb', height: 'calc(100vh - 450px)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                  <TableCell padding="checkbox">
                    <Checkbox />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Team Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Supervisor</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Members</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Loading teams...
                    </TableCell>
                  </TableRow>
                ) : filteredTeams.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No teams found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTeams
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((team) => (
                    <TableRow key={team.id} hover sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                      <TableCell padding="checkbox">
                        <Checkbox />
                      </TableCell>
                      <TableCell sx={{ color: '#374151' }}>{team.name}</TableCell>
                      <TableCell sx={{ color: '#374151' }}>{team.supervisor?.name || 'N/A'}</TableCell>
                      <TableCell sx={{ color: '#374151' }}>{team.members?.length || 0}</TableCell>
                      <TableCell>
                        <Chip
                          label={team.status}
                          size="small"
                          sx={{
                            backgroundColor: team.status === 'active' ? '#e0f2f1' : '#fee2e2',
                            color: team.status === 'active' ? '#0d9488' : '#dc2626',
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleOpenDrawer(team)}
                            sx={{
                              backgroundColor: '#e0f2f1',
                              color: '#0d9488',
                              borderRadius: '6px',
                              textTransform: 'none',
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: '#ccedea',
                              },
                            }}
                          >
                            Update
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleDeleteTeam(team.id)}
                            sx={{
                              backgroundColor: '#fee2e2',
                              color: '#dc2626',
                              borderRadius: '6px',
                              textTransform: 'none',
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: '#fecaca',
                              },
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredTeams.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Showing"
            labelDisplayedRows={({ from, to, count }) =>
              `Showing ${from} to ${to} out of ${count} records`
            }
            sx={{
              '& .MuiTablePagination-selectLabel': {
                color: '#6b7280',
              },
              '& .MuiTablePagination-displayedRows': {
                color: '#6b7280',
              },
              '& .MuiTablePagination-actions': {
                '& .MuiIconButton-root': {
                  color: '#6b7280',
                  '&:hover': {
                    backgroundColor: '#f3f4f6',
                  },
                },
              },
            }}
          />
        </CardContent>
      </Card>
        </Grid>
      </Grid>
      
      {/* Toast Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Drawer for Add/Edit Team */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 800 },
            maxWidth: '100%',
          },
        }}
      >
        <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1f2937' }}>
            {isEditing ? 'Edit Team' : 'Add New Team'}
          </Typography>

          {/* Team Information */}
          <Paper sx={{ p: 3, backgroundColor: '#f9fafb', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
              Team Information
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  name="name"
                  label="Team Name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  options={supervisors}
                  getOptionLabel={(option) => option.name}
                  value={supervisors.find(s => s.id.toString() === formData.supervisor_id) || null}
                  onChange={(_, newValue) => {
                    setFormData(prev => ({ ...prev, supervisor_id: newValue ? newValue.id.toString() : '' }));
                    if (errors.supervisor_id) {
                      setErrors(prev => ({ ...prev, supervisor_id: '' }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Supervisor"
                      required
                      error={!!errors.supervisor_id}
                      helperText={errors.supervisor_id}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  name="notes"
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Team Members */}
          <Paper sx={{ p: 3, backgroundColor: '#f9fafb', mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                Team Members
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddMember}
                size="small"
                sx={{
                  backgroundColor: '#4A90E2',
                  color: 'white',
                  textTransform: 'none',
                }}
              >
                Add Member
              </Button>
            </Box>

            {members.map((member, index) => {
              // Get already selected locations and employees
              const selectedLocations = members
                .filter((m, i) => i !== index && m.location_id)
                .map(m => m.location_id);
              const selectedEmployees = members
                .filter((m, i) => i !== index && m.user_id)
                .map(m => m.user_id);
              
              // Filter options to exclude already selected ones
              const availableLocations = locations.filter(loc => 
                !selectedLocations.includes(loc.id.toString())
              );
              const availableEmployees = employees.filter(emp => 
                !selectedEmployees.includes(emp.id.toString())
              );
              
              return (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Autocomplete
                        options={availableLocations}
                        getOptionLabel={(option) => `${option.name}${option.city ? ` - ${option.city}` : ''}`}
                        value={locations.find(l => l.id.toString() === member.location_id) || null}
                        onChange={(_, newValue) => {
                          handleMemberChange(index, 'location_id', newValue ? newValue.id.toString() : '');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Location"
                            required
                            error={!!errors[`members.${index}.location_id`]}
                            helperText={errors[`members.${index}.location_id`]}
                          />
                        )}
                        disabled={availableLocations.length === 0}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Autocomplete
                        options={availableEmployees}
                        getOptionLabel={(option) => option.name}
                        value={employees.find(e => e.id.toString() === member.user_id) || null}
                        onChange={(_, newValue) => {
                          handleMemberChange(index, 'user_id', newValue ? newValue.id.toString() : '');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Employee"
                            required
                            error={!!errors[`members.${index}.user_id`]}
                            helperText={errors[`members.${index}.user_id`]}
                          />
                        )}
                        disabled={availableEmployees.length === 0}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                      <IconButton
                        onClick={() => handleRemoveMember(index)}
                        disabled={members.length === 1}
                        sx={{
                          color: '#dc2626',
                          '&:disabled': { color: '#9ca3af' },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Paper>

          {/* Footer Actions */}
          <Box sx={{ display: 'flex', gap: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              variant="outlined"
              onClick={handleCloseDrawer}
              sx={{ flex: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              sx={{ flex: 1 }}
            >
              {submitting ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update' : 'Create')}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Teams;

